import { EventCreator, EventInstance, Events } from '@ngrx/signals/events';
import { filter, map, Observable } from 'rxjs';
import { firstRankingFilterOption, RankingFilterOption } from '@kikkz/shared/prediction-game/util';

export interface RankingFilterSequenceConfig<
  OptionsPayload extends { options: RankingFilterOption[] },
  LoadEvent extends EventInstance<string, unknown>,
> {
  /** The store's event service — the same instance its other handlers listen on. */
  events: Events;
  /** Fires when the server's filter options for this ranking have arrived. */
  optionsLoaded: EventCreator<string, OptionsPayload>;
  /** Dispatched for the preselected option; the ranking load hangs off this event. */
  selectOption: EventCreator<string, { option: RankingFilterOption }>;
  /**
   * Optional guard against a late answer of a request the store has moved on from — e.g.
   * the group ranking, where options of a group the user has navigated away from must
   * never overwrite the current selection. Without it every answer is accepted.
   */
  isStillCurrent?: (payload: OptionsPayload) => boolean;
  /**
   * Builds the load event for the selected option. Returning `null` skips the load, for
   * regions that need an anchor the store may have dropped meanwhile (the group id).
   */
  loadRanking: (option: RankingFilterOption) => LoadEvent | null;
}

export interface RankingFilterSequence<LoadEvent extends EventInstance<string, unknown>> {
  /** Options arrived → preselect the option with the lowest `orderIndex`. */
  selectInitialOption$: Observable<EventInstance<string, { option: RankingFilterOption }>>;
  /** Option selected → load that ranking. */
  loadOnSelect$: Observable<LoadEvent>;
}

/**
 * The sequence "server options arrived → preselect the first one → load its ranking",
 * shared by the global ranking and the group ranking.
 *
 * It lives in the store (rather than in a tab) so every consumer of a ranking gets the
 * initial selection for free and no component holds filter state of its own. The two
 * regions differ only in their events and in whether a late answer still counts, which is
 * why this is a factory for the two handler observables rather than a `signalStoreFeature`:
 * each store keeps its own event group, its own handler names and its own devtools entry.
 *
 * An empty option list (season not started) selects nothing and loads nothing — every
 * option the backend accepts has to come from that list, so there is no request to make.
 */
export function rankingFilterSequence<
  OptionsPayload extends { options: RankingFilterOption[] },
  LoadEvent extends EventInstance<string, unknown>,
>(config: RankingFilterSequenceConfig<OptionsPayload, LoadEvent>): RankingFilterSequence<LoadEvent> {
  const { events, optionsLoaded, selectOption, isStillCurrent, loadRanking } = config;

  return {
    selectInitialOption$: events.on(optionsLoaded).pipe(
      filter(({ payload }) => isStillCurrent?.(payload) ?? true),
      map(({ payload }) => firstRankingFilterOption(payload.options)),
      filter((option): option is RankingFilterOption => option !== null),
      map((option) => selectOption({ option })),
    ),
    loadOnSelect$: events.on(selectOption).pipe(
      map(({ payload }) => loadRanking(payload.option)),
      filter((event): event is LoadEvent => event !== null),
    ),
  };
}
